import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  CircularProgress,
  Typography,
} from "@material-ui/core";
import { Resource } from "react-admin";
import { useTokenOps } from "../components/TokenContext";
import { carbonRequest } from "./corporateCarbon/api";
import {
  carbonReferenceChoices,
  fetchCarbonOriginChoices,
} from "./corporateCarbon/referenceData";
import {
  CarbonAdminPage,
  CarbonForm,
  CarbonFormGrid,
  CarbonFormToolbar,
  CarbonSelectInput,
  CarbonTextInput,
} from "./corporateCarbon/CarbonAdminPage";

const emptyProfile = {
  taxId: "",
  mainOriginId: "",
  sector: "",
  reportingContactName: "",
  reportingContactEmail: "",
  employeeFte: "",
  revenueAmount: "",
  revenueCurrency: "",
  totalOwnedSiteArea: "",
  totalOwnedSiteAreaUnitId: "",
};

const profileForm = (profile) =>
  Object.fromEntries(
    Object.keys(emptyProfile).map((key) => [key, profile?.[key] ?? ""]),
  );

const optional = (body, field, value, transform = (current) => current) => {
  if (typeof value === "string" && value.trim() === "") return;
  const transformed = transform(value);
  if (transformed !== "") body[field] = transformed;
};

const invalidNumeric = (value) =>
  value !== "" &&
  (!/^\d+(?:\.\d{1,6})?$/.test(value) ||
    !Number.isFinite(Number(value)) ||
    Number(value) >= 1e18);

export const validateCarbonProfile = (form) => {
  if (!form.mainOriginId) return "Main country is required.";
  const email = form.reportingContactEmail.trim();
  if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
    return "Enter a valid reporting contact email.";
  for (const [field, label] of [
    ["employeeFte", "Employee FTE"],
    ["revenueAmount", "Revenue amount"],
    ["totalOwnedSiteArea", "Total owned site area"],
  ]) {
    if (form[field] !== "" && Number(form[field]) < 0)
      return `${label} cannot be negative.`;
    if (form[field] !== "" && invalidNumeric(form[field]))
      return `${label} must be non-negative, below 10^18, and use at most 6 decimal places.`;
  }
  const currency = form.revenueCurrency.trim();
  if ((form.revenueAmount === "") !== (currency === ""))
    return "Revenue amount and currency must be provided together.";
  if (currency && !/^[A-Z]{3}$/.test(currency))
    return "Revenue currency must be a 3-letter uppercase code.";
  if (
    (form.totalOwnedSiteArea === "") !==
    (form.totalOwnedSiteAreaUnitId === "")
  )
    return "Owned site area and area unit must be provided together.";
  return null;
};

const profilePayload = (form) => {
  const body = { mainOriginId: form.mainOriginId };
  optional(body, "taxId", form.taxId, (value) => value.trim());
  optional(body, "sector", form.sector, (value) => value.trim());
  optional(body, "reportingContactName", form.reportingContactName, (value) =>
    value.trim(),
  );
  optional(body, "reportingContactEmail", form.reportingContactEmail, (value) =>
    value.trim(),
  );
  optional(body, "employeeFte", form.employeeFte, Number);
  optional(body, "revenueAmount", form.revenueAmount, Number);
  optional(body, "revenueCurrency", form.revenueCurrency, (value) =>
    value.trim(),
  );
  optional(body, "totalOwnedSiteArea", form.totalOwnedSiteArea, Number);
  optional(body, "totalOwnedSiteAreaUnitId", form.totalOwnedSiteAreaUnitId);
  return body;
};

const displayValue = (value) =>
  value === "" || value === null || value === undefined ? "—" : String(value);

const choiceName = (choices, id, fallbackLabel) => {
  if (!id) return "—";
  const choice = choices.find((item) => item.id === id);
  if (!choice) return `${fallbackLabel} (${id})`;
  return choice.name || choice.code || choice.label?.en || id;
};

const ProfileDetails = ({ form, origins, units }) => {
  const fields = [
    ["Tax ID", form.taxId],
    ["Main country", choiceName(origins, form.mainOriginId, "Current country")],
    ["Sector", form.sector],
    ["Reporting contact name", form.reportingContactName],
    ["Reporting contact email", form.reportingContactEmail],
    ["Employee FTE", form.employeeFte],
    ["Revenue amount", form.revenueAmount],
    ["Revenue currency", form.revenueCurrency],
    ["Total owned site area", form.totalOwnedSiteArea],
    ["Area unit", choiceName(units, form.totalOwnedSiteAreaUnitId, "Current unit")],
  ];

  return (
    <CarbonFormGrid component="dl" aria-label="Corporate profile details">
      {fields.map(([label, value]) => (
        <div key={label}>
          <Typography component="dt" color="textSecondary" variant="body2">
            {label}
          </Typography>
          <Typography component="dd" style={{ margin: "4px 0 0" }}>
            {displayValue(value)}
          </Typography>
        </div>
      ))}
    </CarbonFormGrid>
  );
};

export const CarbonProfilePage = ({ accessToken }) => {
  const tokenOps = useTokenOps();
  const currentAccessToken = accessToken || tokenOps.account?.accessToken;
  const generationRef = useRef(0);
  const tokenRef = useRef(currentAccessToken);
  tokenRef.current = currentAccessToken;
  const [form, setForm] = useState(emptyProfile);
  const [savedForm, setSavedForm] = useState(emptyProfile);
  const [editing, setEditing] = useState(false);
  const [origins, setOrigins] = useState([]);
  const [units, setUnits] = useState([]);
  const [exists, setExists] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    const generation = ++generationRef.current;
    let active = true;
    setLoading(true);
    setLoadFailed(false);
    setError("");
    setSuccess("");
    setExists(true);
    setForm(emptyProfile);
    setSavedForm(emptyProfile);
    setEditing(false);
    setOrigins([]);
    setUnits([]);
    setSaving(false);
    if (!currentAccessToken) {
      setLoading(false);
      setError("An active account token is required.");
      return () => {
        active = false;
      };
    }
    const profile = carbonRequest(
      currentAccessToken,
      "corporate-carbon/profile",
    ).catch((requestError) => {
      if (
        requestError.status === 404 &&
        (requestError.code === "CARBON_PROFILE_NOT_FOUND" ||
          requestError.message === "CARBON_PROFILE_NOT_FOUND")
      )
        return null;
      throw requestError;
    });
    Promise.all([
      profile,
      fetchCarbonOriginChoices(currentAccessToken),
      carbonReferenceChoices(currentAccessToken, "quantityUnit"),
    ])
      .then(([loadedProfile, loadedOrigins, loadedUnits]) => {
        if (!active || generationRef.current !== generation) return;
        setExists(Boolean(loadedProfile));
        const loadedForm = profileForm(loadedProfile);
        setForm(loadedForm);
        setSavedForm(loadedForm);
        setEditing(false);
        setOrigins(loadedOrigins);
        setUnits(loadedUnits);
      })
      .catch((requestError) => {
        if (active && generationRef.current === generation) {
          setLoadFailed(true);
          setError(requestError.message);
        }
      })
      .finally(() => {
        if (active && generationRef.current === generation) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [currentAccessToken]);

  const change = (event) =>
    setForm((current) => ({
      ...current,
      [event.target.name]: event.target.value,
    }));
  const save = async (event) => {
    event.preventDefault();
    setSuccess("");
    const validation = validateCarbonProfile(form);
    if (validation) {
      setError(validation);
      return;
    }
    setSaving(true);
    setError("");
    const requestToken = currentAccessToken;
    const generation = generationRef.current;
    const current = () =>
      generationRef.current === generation && tokenRef.current === requestToken;
    try {
      const saved = await carbonRequest(
        requestToken,
        "corporate-carbon/profile",
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(profilePayload(form)),
        },
      );
      if (!current()) return;
      setForm(profileForm(saved));
      setSavedForm(profileForm(saved));
      setEditing(false);
      setExists(true);
      setSuccess("Corporate profile saved.");
    } catch (requestError) {
      if (current()) setError(requestError.message);
    } finally {
      if (current()) setSaving(false);
    }
  };

  return (
    <CarbonAdminPage
      title="Corporate carbon profile"
      description="Account-level organization data used by carbon inventories."
      actions={
        !loading && !loadFailed && !editing ? (
          <Button color="primary" onClick={() => setEditing(true)}>
            {exists ? "Edit" : "Create corporate profile"}
          </Button>
        ) : null
      }
    >
        {loading && (
          <div role="status" aria-live="polite">
            <CircularProgress size={20} aria-hidden="true" /> Loading corporate
            profile…
          </div>
        )}
        {!loading && error && (
          <Typography role="alert" color="error">
            {error}
          </Typography>
        )}
        {!loading && !exists && !editing && (
          <Typography>
            No corporate profile exists for this account yet.
          </Typography>
        )}
        {!loading && !loadFailed && exists && !editing && (
          <ProfileDetails form={form} origins={origins} units={units} />
        )}
        {!loading && !loadFailed && editing && (
          <CarbonForm onSubmit={save} noValidate aria-label="Corporate profile form">
            <CarbonTextInput label="Tax ID" name="taxId" value={form.taxId} onChange={change} inputProps={{ maxLength: 255 }} />
            <CarbonSelectInput
              choices={form.mainOriginId && !origins.some((item) => item.id === form.mainOriginId) ? [{ id: form.mainOriginId, name: `Current country (${form.mainOriginId})` }, ...origins] : origins}
              emptyText="Select country"
              label="Main country"
              name="mainOriginId"
              value={form.mainOriginId}
              onChange={change}
              required
            />
            <CarbonTextInput label="Sector" name="sector" value={form.sector} onChange={change} inputProps={{ maxLength: 255 }} />
            <CarbonTextInput label="Reporting contact name" name="reportingContactName" value={form.reportingContactName} onChange={change} inputProps={{ maxLength: 255 }} />
            <CarbonTextInput label="Reporting contact email" name="reportingContactEmail" type="email" value={form.reportingContactEmail} onChange={change} inputProps={{ maxLength: 320 }} />
            <CarbonTextInput label="Employee FTE" name="employeeFte" type="number" value={form.employeeFte} onChange={change} inputProps={{ min: 0, step: "0.000001" }} />
            <CarbonTextInput label="Revenue amount" name="revenueAmount" type="number" value={form.revenueAmount} onChange={change} inputProps={{ min: 0, step: "0.000001" }} />
            <CarbonTextInput label="Revenue currency" name="revenueCurrency" value={form.revenueCurrency} onChange={change} inputProps={{ maxLength: 3 }} />
            <CarbonTextInput label="Total owned site area" name="totalOwnedSiteArea" type="number" value={form.totalOwnedSiteArea} onChange={change} inputProps={{ min: 0, step: "0.000001" }} />
            <CarbonSelectInput
              choices={form.totalOwnedSiteAreaUnitId && !units.some((item) => item.id === form.totalOwnedSiteAreaUnitId) ? [{ id: form.totalOwnedSiteAreaUnitId, name: `Current unit (${form.totalOwnedSiteAreaUnitId})` }, ...units] : units}
              emptyText="Select unit"
              label="Area unit"
              name="totalOwnedSiteAreaUnitId"
              value={form.totalOwnedSiteAreaUnitId}
              onChange={change}
            />
            <CarbonFormToolbar>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={saving}
              >
                {saving ? "Saving…" : "Save corporate profile"}
              </Button>
              <Button onClick={() => { setForm(savedForm); setEditing(false); setError(""); }}>Cancel</Button>
            </CarbonFormToolbar>
          </CarbonForm>
        )}
        {success && (
          <Typography role="status" aria-live="polite">
            {success}
          </Typography>
        )}
    </CarbonAdminPage>
  );
};

const carbonProfileResource = () => (
  <Resource
    name="carbonProfile"
    options={{ label: "Corporate profile", menuGroup: "Carbon Accounting" }}
    list={CarbonProfilePage}
  />
);
export default carbonProfileResource;
